import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChatCircleDots, Stethoscope, TestTube, Pill, ForkKnife, Warning, Sparkle } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const tiles = [
  { to: "/app/ai", icon: ChatCircleDots, title: "Talk to Priya", desc: "Voice or text — I'm listening.", color: "bg-ayurveda text-bone" },
  { to: "/app/doctors", icon: Stethoscope, title: "Find a doctor", desc: "Verified specialists near you.", color: "bg-white" },
  { to: "/app/labs", icon: TestTube, title: "Lab reports", desc: "Upload & get a medical review.", color: "bg-white" },
  { to: "/app/pharmacy", icon: Pill, title: "Pharmacy", desc: "Generic & Jan Aushadhi prices.", color: "bg-white" },
  { to: "/app/nutrition", icon: ForkKnife, title: "Regional nutrition", desc: "Kerala food, evidence-aware.", color: "bg-white" },
  { to: "/app/emergency", icon: Warning, title: "Emergency SOS", desc: "One-tap help & contacts.", color: "bg-terracotta text-white" },
];

export default function Home() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ appointments: 0, reports: 0 });

  useEffect(() => {
    Promise.all([api.get("/appointments/mine"), api.get("/lab-reports/mine")])
      .then(([a, l]) => setStats({ appointments: a.data.length, reports: l.data.length }))
      .catch(() => {});
  }, []);

  return (
    <div className="p-6 md:p-12 pb-24 md:pb-12 max-w-6xl">
      <div className="mb-10">
        <div className="overline text-terracotta">Namaste</div>
        <h1 className="font-heritage text-5xl md:text-6xl text-ayurveda mt-1 leading-tight">
          Hello, {user?.name?.split(" ")[0] || "friend"}.
        </h1>
        <p className="text-charcoal/70 mt-3 max-w-lg">
          How can Priya help you today? You can speak, type, or tap.
        </p>
      </div>

      {/* Snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <div className="rounded-2xl bg-white p-5 border border-black/5">
          <div className="overline text-charcoal/50">Appointments</div>
          <div className="font-clinical text-3xl mt-1 text-ayurveda">{stats.appointments}</div>
        </div>
        <div className="rounded-2xl bg-white p-5 border border-black/5">
          <div className="overline text-charcoal/50">Lab reports</div>
          <div className="font-clinical text-3xl mt-1 text-ayurveda">{stats.reports}</div>
        </div>
        <div className="rounded-2xl bg-white p-5 border border-black/5">
          <div className="overline text-charcoal/50">Region</div>
          <div className="font-clinical text-xl mt-1 text-ayurveda">{user?.profile?.region || "—"}</div>
        </div>
        <div className="rounded-2xl bg-white p-5 border border-black/5">
          <div className="overline text-charcoal/50">Language</div>
          <div className="font-clinical text-xl mt-1 text-ayurveda uppercase">{user?.profile?.language || "en"}</div>
        </div>
      </div>

      {/* Tile grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {tiles.map((t, i) => (
          <motion.div
            key={t.to}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <Link to={t.to} data-testid={`tile-${t.title.toLowerCase().replace(/\s/g,'-')}`}>
              <Card className={`h-full ${t.color} border-black/5 hover:-translate-y-1 transition-transform duration-300 cursor-pointer`}>
                <CardContent className="p-6">
                  <t.icon size={32} weight="duotone" className={`${t.color.includes("bone") || t.color.includes("white") && !t.color.includes("terracotta") ? "text-ayurveda" : ""} mb-4`} />
                  <div className="font-clinical text-lg leading-tight">{t.title}</div>
                  <div className={`text-xs mt-1 ${t.color.includes("white") ? "text-charcoal/60" : "opacity-80"}`}>{t.desc}</div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="mt-12 rounded-3xl bg-ayurveda text-bone p-8 md:p-12 grain relative overflow-hidden">
        <div className="max-w-xl relative z-10">
          <Sparkle size={28} weight="duotone" className="text-terracotta mb-4" />
          <div className="overline text-terracotta">Try today</div>
          <h2 className="font-heritage text-4xl mt-2 leading-tight">Ask Priya about your sleep, stress, or meals — in your language.</h2>
          <Link to="/app/ai">
            <Button className="mt-6 bg-terracotta hover:bg-terracotta/90 text-white h-11 px-8" data-testid="home-cta-ai">
              Start a conversation
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
